/*
 * Task recurrence (a small RFC 5545 RRULE subset: FREQ, INTERVAL, BYDAY, BYMONTHDAY) and completion.
 * Shared by the web app, the server's widget endpoint and anything else that completes tasks,
 * so a recurring task advances the same way everywhere.
 */

export type Repeat = "daily" | "weekdays" | "weekly" | "monthly" | "yearly";

export const REPEAT_RULES: Record<Repeat, string> = {
  daily: "FREQ=DAILY",
  weekdays: "FREQ=WEEKLY;BYDAY=MO,TU,WE,TH,FR",
  weekly: "FREQ=WEEKLY",
  monthly: "FREQ=MONTHLY",
  yearly: "FREQ=YEARLY",
};

export function repeatOf(rrule: string | null | undefined): Repeat | null {
  if (!rrule) return null;
  // BYMONTHDAY is only the remembered day of a monthly/yearly task (see anchorRule), not a different repeat.
  const plain = rrule.replace(/^RRULE:/, "").replace(/;BYMONTHDAY=\d+/, "");
  const hit = (Object.entries(REPEAT_RULES) as [Repeat, string][]).find(([, r]) => r === plain);
  return hit ? hit[0] : null;
}

export function describeRepeat(rrule: string | null | undefined): string {
  const r = repeatOf(rrule);
  if (r) return { daily: "Daily", weekdays: "Every weekday", weekly: "Weekly", monthly: "Monthly", yearly: "Yearly" }[r];
  return rrule ? "Custom" : "";
}

interface Rule {
  freq: "DAILY" | "WEEKLY" | "MONTHLY" | "YEARLY";
  interval: number;
  byDay: number[] | null; // 0 = Sunday
  byMonthDay: number | null; // the day a monthly/yearly task falls on, even after a shorter month
}

const DAYS = ["SU", "MO", "TU", "WE", "TH", "FR", "SA"];

export function parseRRule(rrule: string): Rule | null {
  const parts = new Map(
    rrule
      .replace(/^RRULE:/, "")
      .split(";")
      .map((p) => p.split("=") as [string, string]),
  );
  const freq = parts.get("FREQ");
  if (freq !== "DAILY" && freq !== "WEEKLY" && freq !== "MONTHLY" && freq !== "YEARLY") return null;
  const interval = Math.max(1, Number(parts.get("INTERVAL") ?? 1) || 1);
  const byDay = parts.get("BYDAY")?.split(",").map((d) => DAYS.indexOf(d.slice(-2))).filter((d) => d >= 0) ?? null;
  const md = Number(parts.get("BYMONTHDAY"));
  return { freq, interval, byDay: byDay?.length ? byDay : null, byMonthDay: md >= 1 && md <= 31 ? md : null };
}

function step(d: Date, r: Rule): Date {
  const n = new Date(d);
  switch (r.freq) {
    case "DAILY":
      n.setDate(n.getDate() + r.interval);
      break;
    case "WEEKLY":
      if (r.byDay) {
        // Next listed weekday; crossing into a new week skips (interval-1) weeks.
        for (let i = 1; i <= 7; i++) {
          const c = new Date(d);
          c.setDate(d.getDate() + i);
          if (r.byDay.includes(c.getDay())) {
            if (c.getDay() <= d.getDay() && r.interval > 1) c.setDate(c.getDate() + 7 * (r.interval - 1));
            return c;
          }
        }
      }
      n.setDate(n.getDate() + 7 * r.interval);
      break;
    case "MONTHLY": {
      const day = r.byMonthDay ?? d.getDate();
      n.setDate(1);
      n.setMonth(n.getMonth() + r.interval);
      n.setDate(Math.min(day, daysInMonth(n)));
      break;
    }
    case "YEARLY": {
      const day = r.byMonthDay ?? d.getDate();
      n.setDate(1);
      n.setFullYear(n.getFullYear() + r.interval);
      n.setDate(Math.min(day, daysInMonth(n)));
      break;
    }
  }
  return n;
}

function daysInMonth(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
}

/**
 * The next due time after `from` (local time). Occurrences already in the past are
 * skipped, so completing a long-overdue daily task lands on today or later.
 */
export function nextOccurrence(rrule: string, from: number, now = Date.now()): number | null {
  const r = parseRRule(rrule);
  if (!r) return null;
  const today = new Date(now);
  const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
  let d = step(new Date(from), r);
  for (let i = 0; i < 5000 && d.getTime() < startOfToday; i++) d = step(d, r);
  return d.getTime();
}

/**
 * A monthly or yearly task due on the 29th–31st remembers that day in the rule (BYMONTHDAY), so after a
 * shorter month (Jan 31 → Feb 28) it returns to the 31st instead of staying on the 28th. A remembered day
 * that no longer matches the due date (the user moved it) is replaced.
 */
export function anchorRule(rrule: string, dueAt: number): string {
  const r = parseRRule(rrule);
  if (!r || (r.freq !== "MONTHLY" && r.freq !== "YEARLY")) return rrule;
  const d = new Date(dueAt);
  if (r.byMonthDay != null && Math.min(r.byMonthDay, daysInMonth(d)) === d.getDate()) return rrule;
  const plain = rrule.replace(/;BYMONTHDAY=\d+/, "");
  return d.getDate() > 28 ? `${plain};BYMONTHDAY=${d.getDate()}` : plain;
}

export interface TaskLike {
  completed_at: number | null;
  due_at: number | null;
  reminder_at: number | null;
  recurrence: string | null;
}

/**
 * Fields to write when the user ticks (or unticks) a task. A recurring task with a due
 * date stays open and moves to its next occurrence; its reminder moves by the same amount.
 */
export function toggleCompletion(task: TaskLike, now = Date.now()): Partial<TaskLike> & { updated: number } {
  if (task.completed_at != null) return { completed_at: null, updated: now };
  if (task.recurrence && task.due_at != null) {
    const rule = anchorRule(task.recurrence, task.due_at);
    const next = nextOccurrence(rule, task.due_at, now);
    if (next != null) {
      const shift = next - task.due_at;
      return { due_at: next, reminder_at: task.reminder_at != null ? task.reminder_at + shift : null, ...(rule !== task.recurrence && { recurrence: rule }), updated: now };
    }
  }
  return { completed_at: now, updated: now };
}
